import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const circleGrow = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0); }
  100% { transform: translate(-50%, -50%) scale(1); }
`;

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } },
};

const stats = [
  { label: "Experience", value: "4 Years" },
  { label: "Focus", value: "React / TypeScript" },
  { label: "Availability", value: "Open to Relocate" },
  { label: "Location", value: "India" },
];

function Banner() {
  const scrollToSection = () => {
    window.scrollTo({ top: 500, left: 0, behavior: "smooth" });
  };

  return (
    <Section>
      <motion.div
        className="page"
        key="index"
        exit={{ width: "100%", transition: { duration: 1 } }}
      />
      <motion.div
        className="page2"
        animate={{
          left: "auto",
          right: 0,
          width: "0",
          transition: { duration: 0.5 },
        }}
      />

      <Circle />

      <Container
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit={{ x: "100%", transition: { duration: 1 } }}
      >
        <motion.div variants={fadeIn}>
          <RoleLabel>
            Frontend Engineer&nbsp;
            <At>·</At>
            &nbsp;React / TypeScript&nbsp;
            <At>·</At>
            &nbsp;Open to Relocation
          </RoleLabel>
        </motion.div>

        <HeroRow>
          <HeroLeft>
            <motion.div variants={fadeUp}>
              <NameBlock>
                <FirstName>
                  Savad<Dot>.</Dot>
                </FirstName>
                <SubName>Mohammad Savad N</SubName>
              </NameBlock>
            </motion.div>

            <motion.div variants={fadeUp}>
              <Bio>
                4 years shipping scalable React &amp; TypeScript applications
                35% faster load times, 60% quicker deployments, live across 15+
                countries. Ready to relocate globally for the right team.
              </Bio>
            </motion.div>

            <motion.div variants={fadeUp}>
              <Actions>
                <Anchor to="/work">
                  See My Work
                  <svg width="50" height="30" viewBox="0 0 10 6" fill="none">
                    <path
                      d="M6.71627 0L6.27434 0.441934L8.14928 2.31688H0V2.94189H8.14922L6.27434 4.81678L6.71627 5.25871L9.34566 2.62936L6.71627 0Z"
                      fill="currentColor"
                    />
                  </svg>
                </Anchor>

                <SocialList>
                  <SocialItem>
                    <SocialAnchor
                      href="https://www.linkedin.com/in/savadneeliyan/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      LinkedIn
                    </SocialAnchor>
                  </SocialItem>
                  <Slash>/</Slash>
                  <SocialItem>
                    <SocialAnchor
                      href="https://github.com/savadneeliyan"
                      target="_blank"
                      rel="noreferrer"
                    >
                      GitHub
                    </SocialAnchor>
                  </SocialItem>
                </SocialList>
              </Actions>
            </motion.div>
          </HeroLeft>

          <HeroRight>
            <Img src="/Images/bg.webp" alt="Mohammad Savad" />
            <Bar1 />
            <Bar2 />
          </HeroRight>
        </HeroRow>

        <motion.div variants={fadeUp}>
          <StatsRow>
            {stats.map((s, i) => (
              <React.Fragment key={s.label}>
                <StatItem>
                  <StatLabel>{s.label}</StatLabel>
                  <StatValue>{s.value}</StatValue>
                </StatItem>
                {i < stats.length - 1 && <StatDivider />}
              </React.Fragment>
            ))}
          </StatsRow>
        </motion.div>

        <ScrollDown onClick={scrollToSection}>
          Scroll Down
          <svg width="40" height="24" viewBox="0 0 10 6" fill="none">
            <path
              d="M6.71627 0L6.27434 0.441934L8.14928 2.31688H0V2.94189H8.14922L6.27434 4.81678L6.71627 5.25871L9.34566 2.62936L6.71627 0Z"
              fill="currentColor"
            />
          </svg>
        </ScrollDown>
      </Container>
    </Section>
  );
}

export default Banner;

const Section = styled.div`
  height: 100vh;
  background-color: var(--bg);
  color: var(--text);
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  transition: background-color 0.3s ease, color 0.3s ease;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100svh;
    padding-top: 110px;
    padding-bottom: 60px;
  }
`;

const Container = styled(motion.div)`
  max-width: 1200px;
  width: 100%;
  margin: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  z-index: 2;

  @media (max-width: 1430px) {
    padding: 0 30px;
  }
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const RoleLabel = styled.p`
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-2);
  margin-bottom: 28px;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 11px;
    margin-bottom: 20px;
  }
`;

const At = styled.span`
  // color: #3b82f6;
`;

const HeroRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
  }
`;

const HeroLeft = styled.div`
  flex: 1;
  position: relative;
  z-index: 2;
`;

const HeroRight = styled.div`
  position: relative;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NameBlock = styled.div`
  margin-bottom: 24px;
`;

const FirstName = styled.h1`
  font-size: clamp(56px, 7vw, 96px);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -3px;
  color: var(--text);
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const Dot = styled.span`
  // color: #3b82f6;
  font-size: 1em;
`;

const SubName = styled.p`
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.1em;
  color: var(--text-2);
  opacity: 0.45;
  margin-top: 10px;
  text-transform: uppercase;
  transition: color 0.3s ease;
`;

const Bio = styled.p`
  font-size: clamp(15px, 1.4vw, 17px);
  line-height: 1.75;
  color: var(--text-2);
  font-weight: 300;
  max-width: 560px;
  margin-bottom: 36px;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

const Anchor = styled(Link)`
  text-decoration: none;
  font-size: 18px;
  font-weight: 500;
  position: relative;
  background: none;
  border: none;
  outline: none;
  color: var(--text);
  display: flex;
  padding: 16px 32px;
  z-index: 1;
  margin-left: 20px;
  width: fit-content;
  align-items: center;
  gap: 14px;
  transition: color 0.3s ease;

  &::after {
    position: absolute;
    content: "";
    width: 56px;
    height: 56px;
    background: var(--btn-bg);
    z-index: -1;
    display: block;
    left: -12px;
    top: 0;
    bottom: 0;
    margin: auto 0;
    transition: 0.8s ease;
    border-radius: 50%;
  }
  &:hover::after {
    width: 280px;
    height: 100%;
    border-radius: 40px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    margin-left: 0;
    padding: 14px 24px;
    &::after {
      left: 0;
    }
  }
`;

const SocialList = styled.ul`
  display: flex;
  align-items: center;
  gap: 12px;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SocialItem = styled.li`
  cursor: pointer;
`;

const SocialAnchor = styled.a`
  text-decoration: none;
  color: var(--text-2);
  font-size: 16px;
  transition: color 0.3s ease, transform 0.3s ease;
  display: block;
  position: relative;

  &:hover {
    transform: translateX(6px);
    color: var(--text);
  }
  &::before {
    content: "";
    position: absolute;
    width: 0%;
    background: var(--text);
    height: 1.5px;
    top: 50%;
    left: -4px;
    display: block;
    transition: 0.4s linear;
  }
  &:hover::before {
    width: 115%;
    max-width: 120px;
  }
`;

const Slash = styled.span`
  font-size: 18px;
  font-weight: 300;
  color: var(--text-2);
  opacity: 0.4;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: stretch;
  margin-top: 56px;
  border-top: 1px solid var(--border, rgba(255, 255, 255, 0.1));
  padding-top: 32px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 24px;
    border-top: 1px solid var(--border, rgba(255, 255, 255, 0.1));
    padding-top: 28px;
    margin-top: 36px;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;

  @media (max-width: 768px) {
    flex: 0 0 calc(50% - 12px);
  }
`;

const StatLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-2);
  opacity: 0.6;
  transition: color 0.3s ease;
`;

const StatValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: -0.3px;
  transition: color 0.3s ease;
`;

const StatDivider = styled.div`
  width: 1px;
  background: var(--border, rgba(255, 255, 255, 0.1));
  margin: 0 40px;
  align-self: stretch;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Circle = styled.div`
  position: absolute;
  height: 480px;
  width: 480px;
  outline: 2px solid var(--border);
  outline-offset: -2px;
  border-radius: 50%;
  animation: ${circleGrow} 1.2s ease-out both;
  background-color: #3a3a3a;
  top: 50%;
  left: 50%;
  z-index: 0;

  &::before {
    content: "";
    display: block;
    height: 480px;
    width: 480px;
    background: var(--bg);
    border: 2px solid var(--border);
    border-radius: 50%;
    animation: growDown 1.2s ease-in;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Img = styled.img`
  position: relative;
  z-index: 2;
  animation: img 2s;
  width: 250px;
`;

const Bar = styled.div`
  width: 550px;
  height: 60px;
  position: absolute;
  top: 200px;
  left: -140px;
  z-index: 0;

  &::before {
    content: "";
    width: 100%;
    height: 100%;
    display: block;
    background: var(--bar-bg);
  }
`;

const Bar1 = styled(Bar)`
  transform: rotate(-45deg);
  &::before {
    animation: width 0.8s ease-in;
  }
`;

const Bar2 = styled(Bar)`
  transform: rotate(45deg);
  &::before {
    animation: width 0.5s ease-in;
  }
`;

const ScrollDown = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  font-size: 16px;
  transition: color 0.3s ease, 0.5s linear;
  position: absolute;
  transform: rotate(90deg);
  right: 0%;
  bottom: -20px;
  color: var(--text);
  cursor: pointer;

  @media (max-width: 1600px) {
    display: none;
  }
  @media (min-width: 1600px) {
    right: -15%;
  }
`;
